import { Link } from 'react-router-dom';
import { TRACKED_REG } from '../lib/flightTrack';
import { CalendarPlus, ChevronRight, ClipboardList } from 'lucide-react';

/**
 * The two things you actually come here to do, as one control surface.
 *
 * Book and Log are the same activity at opposite ends of a flight and carry equal
 * weight. They sit as a matched pair with a gap between them — a hairline divider
 * inside one block read as too tight to tap confidently. Book is first because you
 * book before you fly and log after.
 *
 * Previously Log was a full hero and Book was a small card underneath it on the
 * lessee dashboard — and missing entirely from the owner's, which meant the
 * owner had to go find the booking page through the nav every time.
 */
// Routes differ by role and are NOT derivable from a prefix — the lessee logs at
// /flights/new while the owner logs at /admin/log-flight — so both are passed in.
export function FlightActions({ bookTo, logTo }: { bookTo: string; logTo: string }) {
  return (
    <div className="space-y-3">
      <Row
        to={bookTo}
        icon={<CalendarPlus size={26} />}
        title="Book a flight"
        desc={`Reserve ${TRACKED_REG}. Open times confirm instantly.`}
      />
      <Row
        to={logTo}
        icon={<ClipboardList size={26} />}
        title="Log a flight"
        desc="Just landed? Record your Hobbs and tach."
      />
    </div>
  );
}

function Row({
  to,
  icon,
  title,
  desc,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 overflow-hidden rounded-2xl bg-brand p-5 text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift sm:p-6"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="font-display text-xl font-bold leading-tight sm:text-2xl">{title}</div>
        <div className="mt-0.5 text-sm text-sky-100">{desc}</div>
      </div>
      <ChevronRight size={22} className="shrink-0 text-sky-100 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}
